import { supabase } from "./supabase";

const getEdgeFunctionErrorMessage = async (error) => {
  if (!error) {
    return "Unknown edge function error";
  }

  if (error.context && typeof error.context.json === "function") {
    try {
      const errorData = await error.context.json();
      return (
        errorData.error ||
        errorData.message ||
        errorData.details ||
        error.message ||
        "Edge function request failed"
      );
    } catch (contextError) {
      console.error("❌ Failed to parse edge function error response:", contextError);
    }
  }

  return error.message || "Edge function request failed";
};

/**
 * Wise Recipient Service
 * Handles integration with Wise API for recipient management
 * Based on: https://docs.wise.com/api-docs/api-reference/recipient
 */

const wiseRecipientService = {
  /**
   * Get all recipients for an affiliate from Wise
   * @param {string} affiliateName - The affiliate name
   * @returns {Promise<{success: boolean, data?: any[], error?: string}>}
   */
  async getRecipients(affiliateName) {
    try {
      console.log("🔍 Getting Wise recipients for:", affiliateName);

      // First, get the recipient IDs from our database
      const { data: affiliateAccounts, error: dbError } = await supabase
        .from('affiliate_wise_accounts')
        .select('wise_recipient_id')
        .eq('affiliate_name', affiliateName);

      if (dbError) {
        console.error("❌ Database error:", dbError);
        return { success: false, error: dbError.message };
      }

      const recipientIds = affiliateAccounts?.map(acc => acc.wise_recipient_id) || [];
      console.log(`📊 Found ${recipientIds.length} recipient IDs for ${affiliateName}:`, recipientIds);

      if (recipientIds.length === 0) {
        console.log(`ℹ️ No recipients found for affiliate: ${affiliateName}`);
        return { success: true, data: [] };
      }

      // Now call the Edge Function with specific recipient IDs
      const { data, error } = await supabase.functions.invoke("wise-recipients", {
        body: {
          action: "list",
          recipient_ids: recipientIds,
        },
      });

      if (error) {
        console.error("❌ Error getting Wise recipients:", error);
        throw new Error(await getEdgeFunctionErrorMessage(error));
      }

      console.log("✅ Wise recipients retrieved:", data);
      return { success: true, data: data.recipients || [] };
    } catch (error) {
      console.error("❌ Error in getRecipients:", error);
      return { success: false, error: await getEdgeFunctionErrorMessage(error) };
    }
  },

  /**
   * Create a new recipient in Wise
   * @param {string} affiliateName - The affiliate name
   * @param {object} recipientData - The recipient data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async createRecipient(affiliateName, recipientData) {
    try {
      console.log("🚀 Creating Wise recipient for:", affiliateName, recipientData);

      const { data, error } = await supabase.functions.invoke("wise-recipients", {
        body: {
          action: "create",
          affiliateName: affiliateName,
          recipientData: recipientData,
        },
      });

      if (error) {
        console.error("❌ Error creating Wise recipient:", error);
        throw new Error(await getEdgeFunctionErrorMessage(error));
      }

      console.log("✅ Wise recipient created:", data);
      return { success: true, data: data.recipient };
    } catch (error) {
      console.error("❌ Error in createRecipient:", error);
      return { success: false, error: await getEdgeFunctionErrorMessage(error) };
    }
  },

  /**
   * Update an existing recipient in Wise
   * @param {string} affiliateName - The affiliate name
   * @param {number} recipientId - The Wise recipient ID
   * @param {object} recipientData - The updated recipient data
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async updateRecipient(affiliateName, recipientId, recipientData) {
    try {
      console.log("🔄 Updating Wise recipient:", recipientId, recipientData);

      const { data, error } = await supabase.functions.invoke("wise-recipients", {
        body: {
          action: "update",
          affiliateName: affiliateName,
          recipientId: recipientId,
          recipientData: recipientData,
        },
      });

      if (error) {
        console.error("❌ Error updating Wise recipient:", error);
        throw new Error(await getEdgeFunctionErrorMessage(error));
      }

      console.log("✅ Wise recipient updated:", data);
      return { success: true, data: data.recipient };
    } catch (error) {
      console.error("❌ Error in updateRecipient:", error);
      return { success: false, error: await getEdgeFunctionErrorMessage(error) };
    }
  },

  /**
   * Delete a recipient from Wise
   * @param {string} affiliateName - The affiliate name
   * @param {number} recipientId - The Wise recipient ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async deleteRecipient(affiliateName, recipientId) {
    try {
      console.log("🗑️ Deleting Wise recipient:", recipientId);

      const { error } = await supabase.functions.invoke("wise-recipients", {
        body: {
          action: "delete",
          affiliateName: affiliateName,
          recipientId: recipientId,
        },
      });

      if (error) {
        console.error("❌ Error deleting Wise recipient:", error);
        throw new Error(await getEdgeFunctionErrorMessage(error));
      }

      console.log("✅ Wise recipient deleted");
      return { success: true };
    } catch (error) {
      console.error("❌ Error in deleteRecipient:", error);
      return { success: false, error: await getEdgeFunctionErrorMessage(error) };
    }
  },

  /**
   * Format Wise recipient data for display
   * @param {object} recipient - The Wise recipient object
   * @returns {object} Formatted display data
   */
  formatRecipientDisplay(recipient) {
    if (!recipient) return null;

    const details = recipient.details || {};
    const address = details.address || {};

    // Helper function to mask account number - show only last 4 digits
    const maskAccountNumber = (accountNumber) => {
      if (!accountNumber || accountNumber.length <= 4) {
        return "****";
      }
      return "****" + accountNumber.slice(-4);
    };

    // Extract account information based on type - only show last 4 digits
    let bankingInfo = "";
    let accountType = recipient.type || "unknown";

    switch (accountType.toLowerCase()) {
      case "aba":
        bankingInfo = `Account: ****${maskAccountNumber(details.accountNumber).slice(-4)}`;
        break;
      case "iban":
        bankingInfo = `IBAN: ****${maskAccountNumber(details.iban).slice(-4)}`;
        break;
      case "canadian":
      case "bank_account":
        bankingInfo = `Account: ****${maskAccountNumber(details.accountNumber).slice(-4)}`;
        break;
      case "australian":
        bankingInfo = `Account: ****${maskAccountNumber(details.accountNumber).slice(-4)}`;
        break;
      case "indian":
        bankingInfo = `Account: ****${maskAccountNumber(details.accountNumber).slice(-4)}`;
        break;
      default:
        bankingInfo = details.accountNumber ? `Account: ****${maskAccountNumber(details.accountNumber).slice(-4)}` : "Account";
    }

    return {
      id: recipient.id,
      displayName: recipient.accountHolderName || recipient.name || "Unknown Account",
      bankingInfo: bankingInfo,
      currency: recipient.currency || "USD",
      country: address.country || "US",
      isActive: recipient.active !== false,
      raw: recipient, // Keep original data for processing
    };
  },

  /**
   * Check if an affiliate is registered in Wise
   * @param {string} affiliateName - The affiliate name
   * @returns {Promise<{success: boolean, isRegistered: boolean, error?: string}>}
   */
  async checkAffiliateRegistration(affiliateName) {
    try {
      console.log("🔍 Checking Wise registration for:", affiliateName);

      const result = await this.getRecipients(affiliateName);
      
      if (result.success) {
        // If we can get recipients without error, affiliate is registered
        return { 
          success: true, 
          isRegistered: true,
          recipientCount: result.data.length 
        };
      } else {
        // Check if error indicates affiliate is not registered
        const errorMessage = result.error.toLowerCase();
        if (errorMessage.includes("not found") || 
            errorMessage.includes("not registered") ||
            errorMessage.includes("profile not found")) {
          return { 
            success: true, 
            isRegistered: false 
          };
        } else {
          // Other error - return as error
          return { 
            success: false, 
            error: result.error 
          };
        }
      }
    } catch (error) {
      console.error("❌ Error checking affiliate registration:", error);
      return { success: false, error: error.message };
    }
  },
};

export { wiseRecipientService };
